import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  TextField,
  TextFieldProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useRef, useState } from 'react';
import PageTitle from '../../components/page-title/PageTitle';
import { Container } from './FollowupList.styled';
import * as yup from 'yup';
import IFormError from '../../interfaces/IFormError';
import { useValidateSchema } from '../../hooks/providers/useValidateSchema';

import { AiFillCloseCircle } from 'react-icons/ai';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import { InputFile } from '../wizard/steps/medicalCertificateImage/MedicalCertificateImage.styles';
import imageCompression from 'browser-image-compression';
import { Delete as DeleteIcon } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { sdsGrayscale } from '../../theme/sds-tokens';
import toastr from 'toastr';
import IMedicalCertificate from '../../interfaces/IMedicalCertificate';
import { useMedicalCertificateService } from '../../hooks/services/useMedicalCertificateService';

const formatFileSize = (bytes: number): string =>
  bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

interface IProps {
  closeDialog: () => void;
  isOpen: boolean;
  medicalCertificate: IMedicalCertificate | null;
  handleRequest: (message: string) => Promise<void>;
}

const FollowupEditDeadline = (props: IProps) => {
  const handleClose = () => {
    setErrors(null);
    setJustification('');
    setDateDeadline(null);
    setFiles([]);
    setLoading(false);
    props.closeDialog();
  };
  const request = useMedicalCertificateService();

  const [errors, setErrors] = useState<IFormError | null>(null);
  const [justification, setJustification] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [medicalCertificate, setMedicalCertificate] = useState<IMedicalCertificate | null>(props.medicalCertificate);
  const [dateDeadline, setDateDeadline] = useState<Moment | null>(null);
  const inputFile = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<File[]>([]);

  const validation = useValidateSchema();

  const schema = yup.object({
    justification: yup
      .string()
      .required('Campo obrigatório')
      .min(1, 'Campo obrigatório'),
    documentDate: yup
      .date()
      .typeError('Formato de data inválido. Use: DD/MM/YYYY')
      .required('Data obrigatória'),
  });

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    const file = e.target.files?.item(0);
    if (file) {
      setFiles(prevFiles => [...prevFiles, file]);
    }
    setLoading(false);
  };

  const sendFile = async (fileParam: File | null) => {
    if (!fileParam) return;

    setLoading(true);
    let file = fileParam;

    if (file.size > 5242880) {
      setLoading(false);
      toastr.error('O tamanho máximo da imagem é 5MB');
      return;
    }

    if (file.size >= 1000000 && file.size <= 5242880 && fileParam.type !== 'application/pdf') {
      const options = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true };
      file = await imageCompression(fileParam, options);
    }

    const data = new FormData();
    data.append('attachment', file, file.name);

    await request
      .sendAttachment(`${props.medicalCertificate?.id}`, data)
      .then(() => {
        setLoading(false);
      })
      .catch(err => {
        console.log(err);
        toastr.error(err.message);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const updateDeadline = async (
    idMedicalCertificate: string | undefined,
    justification: string,
    dateFormated: string,
  ) => {
    if (!idMedicalCertificate) {
      toastr.error('Selecione uma imagem ao menos');
      setLoading(false);
      return;
    }
    setLoading(true);

    await request
      .updateDeadline(idMedicalCertificate, justification, dateFormated)
      .then(() => {
        setLoading(false);
        setJustification('');
        setDateDeadline(null);
        props.closeDialog();
        toastr.success('Prazo final atualizado com sucesso!');
      })
      .catch((err: { message: any }) => {
        toastr.error(err.message);

        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEndDate = async () => {
    const dateFormated = moment(dateDeadline).toISOString();
    setLoading(true);

    const result = await validation.validate(schema, {
      justification,
      documentDate: dateDeadline?.toDate(),
    });

    setErrors(result.errors);

    if (!result.isValid) {
      setLoading(false);
      return;
    }

    setMedicalCertificate({
      ...medicalCertificate!,
      endTime: moment(dateDeadline).format('YYYY-MM-DD'),
    });

    try {
      await updateDeadline(props.medicalCertificate?.id, justification, dateFormated);

      await Promise.all(files.map(file => sendFile(file)));
      return true;
    } catch (err) {
      toastr.error((err as any)?.message || 'Erro ao atualizar o prazo.');
      return false;
    } finally {
      setLoading(false);
    }
  };
  const handleSubmit = async () => {
    if (files.length === 0) {
      toastr.error('Selecione pelo menos uma foto ou documento.');
      return;
    }
    await updateEndDate();
  };

  const removeAttachmentByIndex = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Container>
      <PageTitle title="Alterar Prazo" icon={<EditIcon sx={{ color: 'primary.main' }} />}>
        <Tooltip title="Fechar Alteração Prazo" placement="left">
          <IconButton onClick={handleClose}>
            <AiFillCloseCircle />
          </IconButton>
        </Tooltip>
      </PageTitle>

      <Grid container spacing={2} sx={{ padding: '20px 0' }}>
        <Grid item sm={12}>
          <TextField
            label="Justificativa"
            placeholder="Informe porque a data será alterada"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={justification}
            onChange={e => setJustification(e.target.value)}
            error={Boolean(errors?.justification)}
            helperText={errors?.justification}
            autoFocus
          />
        </Grid>

        <Grid item sm={12}>
          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="pt-br">
            <DatePicker
              label="Nova data final do documento"
              value={dateDeadline}
              onChange={date => setDateDeadline(date)}
              renderInput={(params: TextFieldProps) => (
                <TextField
                  {...params}
                  fullWidth
                  InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                  error={Boolean(errors?.documentDate)}
                  helperText={errors?.documentDate}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      <Box
        component="label"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          padding: 3,
          borderRadius: 1,
          border: '1px dashed',
          borderColor: 'primary.main',
          bgcolor: theme => alpha(theme.palette.primary.main, 0.06),
          cursor: 'pointer',
          transition: 'background-color 200ms ease-out',
          '&:hover': { bgcolor: theme => alpha(theme.palette.primary.main, 0.12) },
        }}
      >
        <InputFile type="file" ref={inputFile} onChange={onFileChange} accept="application/pdf,image/*" />
        <CameraAltOutlinedIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="sectionTitle">Escolher foto</Typography>
        <Typography
          variant="helper"
          sx={{ color: theme => (theme.palette.mode === 'dark' ? sdsGrayscale[40] : sdsGrayscale[60]) }}
        >
          Imagem ou PDF, até 5MB
        </Typography>
      </Box>

      {files.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, marginTop: 2 }}>
          {files.map((file, index) => (
            <Box
              key={`${file.name}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                paddingX: 1.5,
                paddingY: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
                  color: 'primary.main',
                }}
              >
                {file.type.startsWith('image/') ? (
                  <ImageOutlinedIcon fontSize="small" />
                ) : (
                  <InsertDriveFileOutlinedIcon fontSize="small" />
                )}
              </Box>

              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="labels" noWrap sx={{ display: 'block', fontWeight: 600 }}>
                  {file.name}
                </Typography>
                <Typography
                  variant="helper"
                  sx={{ display: 'block', color: theme => (theme.palette.mode === 'dark' ? sdsGrayscale[40] : sdsGrayscale[60]) }}
                >
                  {formatFileSize(file.size)}
                </Typography>
              </Box>

              <Tooltip title="Remover anexo" placement="left">
                <IconButton onClick={() => removeAttachmentByIndex(index)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}
      {loading && <LinearProgress />}

      <Divider sx={{ marginTop: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
        <Button onClick={handleClose} variant="contained" color="secondary" sx={{ marginRight: 2 }}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSubmit}>
          Confirmar
        </Button>
      </Box>
    </Container>
  );
};

export default FollowupEditDeadline;
